/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import type { Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import type { History } from 'history';
import type { GraphQLFormattedError } from 'graphql';

import { clearSpyeeTokens, clearTokens, isSpyeeMode } from './storage';

import {
    ARCHIVED_CODE,
    AUTHENTICATION_REQUIRED_CODE,
    AUTHENTICATION_REQUIRED_MESSAGE,
    BANNED_CODE,
    EXPIRED_TOKEN_CODE,
} from './auth-errors';

// Le client ne remonte que ce que l'API ne voit pas ou rejette avant de l'exécuter : l'API capture déjà ses
// propres erreurs, et un refus métier n'est pas un bug.
const CONTRACT_ERROR_CODES = new Set(['GRAPHQL_PARSE_FAILED', 'GRAPHQL_VALIDATION_FAILED', 'BAD_USER_INPUT']);

type ErrorHandlerArgs = {
    graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
    networkError?: Error | null;
    operation?: Operation;
};

function isSubscription(operation?: Operation): boolean {
    if (!operation) {
        return false;
    }
    const definition = getMainDefinition(operation.query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}

export const createErrorHandler =
    (history: History) =>
    ({ graphQLErrors, networkError, operation }: ErrorHandlerArgs): void => {
        if (!graphQLErrors?.length && networkError) {
            // Coupure isolée (Failed to fetch, Load failed) : pas un bug, seulement le contexte des events suivants.
            Sentry.addBreadcrumb({
                category: 'graphql',
                level: 'warning',
                message: `[NetworkError] ${networkError.message}`,
                data: { operationName: operation?.operationName },
            });
        }

        if (graphQLErrors && graphQLErrors.length > 0) {
            const [error] = graphQLErrors;
            const { message, locations, path } = error;

            const formattedMessage = [
                `[GraphQLError] ${message}`,
                `Location: ${JSON.stringify(locations)}`,
                `Path: ${path}`,
            ].join('\n');

            const code = error.extensions?.code;
            const isUnauthenticated =
                code === AUTHENTICATION_REQUIRED_CODE || error.message === AUTHENTICATION_REQUIRED_MESSAGE;

            // An expired or unrecognised access token is self-healing: the token refresh link renews
            // the token and replays the request, logs out only if the refresh itself is refused, and
            // reports that once. Reporting every occurrence buried real GraphQL errors under thousands
            // of duplicate events (Sentry TOTAPP-3DM). A breadcrumb keeps the context on the errors that
            // follow. Mirrors SILENT_ERROR_CODES in systems/api/src/middlewares/sentry.ts.
            if (code === EXPIRED_TOKEN_CODE || isUnauthenticated) {
                Sentry.addBreadcrumb({ category: 'graphql', level: 'info', message: formattedMessage });
                // Pas un `console.error` : rien n'est cassé pour l'utilisateur — le lien de refresh renouvelle
                // le token et rejoue la requête. En rouge, ces messages (une ligne par requête en vol au moment
                // de l'expiration) noyaient les vraies erreurs. `console.debug` reste lisible en niveau
                // « Verbose » pour déboguer une boucle de refresh.
                console.debug(formattedMessage);
            } else if (isSubscription(operation) || (typeof code === 'string' && CONTRACT_ERROR_CODES.has(code))) {
                // Une subscription ne passe pas par le plugin Sentry de l'API : le client est seul à la voir échouer.
                Sentry.captureEvent({
                    level: 'error',
                    message: formattedMessage,
                    tags: { operationName: operation?.operationName },
                    contexts: { graphqlError: { ...error } },
                });
                console.error(formattedMessage);
            } else {
                Sentry.addBreadcrumb({ category: 'graphql', level: 'warning', message: formattedMessage });
                console.warn(formattedMessage);
            }

            if (code === BANNED_CODE || code === ARCHIVED_CODE) {
                if (isSpyeeMode()) {
                    // En mode espion, le compte fermé est celui qu'on espionne (le header `spiedusertoken`
                    // l'emporte côté API) : on sort du mode espion, la session de l'opérateur reste intacte.
                    clearSpyeeTokens();
                    history.replace(`/spying?reason=${code}`);
                } else {
                    // Le refresh token aussi : laissé en place, il rouvrait une session au prochain refresh.
                    clearTokens();
                    history.replace(`/sven?reason=${code}`);
                }
                // TODO should not be required, but app does not rerender on location change
                history.go(0);
            }
        }
    };
